import {alpha, SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

/**
 * The meanings this area needs a colour for, each mapped onto a pairing the application already uses.
 *
 * Every value comes from the theme, and every one has a precedent on another screen, so these states
 * read correctly to anyone who uses the rest of the product before they read a word of the labels.
 * `brand` is deliberately absent: it belongs to the one primary action on a page, and using it as a
 * semantic colour is what makes a screen feel like it is shouting.
 *
 * Note `text.secondary` is `#fff` in this theme — the colour for text on a dark surface, not for
 * de-emphasis. Nothing here may use it, and nothing else in this area should either.
 */
export const IAM_TONE = {
    /** Allowed. Precedent: the RFID table's active tag. */
    granted: {bg: alpha(theme.palette.success.dark, 0.1), fg: theme.palette.success.dark},
    /** Refused or taken away. Precedent: the RFID table's blocked tag. */
    refused: {bg: alpha(theme.palette.error.dark, 0.1), fg: theme.palette.error.dark},
    /** Owned by the platform, read-only here. */
    platform: {bg: theme.palette.secondaryColor[50], fg: theme.palette.secondaryColor[700]},
    /** The lock-out guard. */
    protectedTone: {bg: theme.palette.alternative[50], fg: theme.palette.alternative[700]},
    /** This business's own. */
    yours: {bg: theme.palette.primaryColor[50], fg: theme.palette.primaryColor[700]},
    /** Zero, or simply unremarkable. */
    none: {bg: theme.palette.neutral[100], fg: theme.palette.neutral[700]},
} as const;

export type IamTone = (typeof IAM_TONE)[keyof typeof IAM_TONE];

/** A rounded pill. Precedent: the charger status pill and the configuration boolean pill. */
export const pillSx = (tone: IamTone): SxProps<Theme> => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1.5,
    py: 0.5,
    borderRadius: 999,
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.6,
    whiteSpace: 'nowrap',
    background: tone.bg,
    color: tone.fg,
});

/** A banner that explains something, tinted by what it is explaining. */
export const calloutSx = (tone: IamTone): SxProps<Theme> => ({
    p: 2,
    mt: 3,
    mb: 1.5,
    borderRadius: '14px',
    bgcolor: '#F4F6F9',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    borderLeft: `3px solid ${tone.fg}`,
});

/**
 * A column heading over a list of rows.
 *
 * Quiet and small on purpose: it exists to say what the value beneath it means, not to compete with it.
 * A badge with no heading reads as decoration — "PRIVATE DRIVER" beside a name could be a role, a plan
 * or a status, and the one thing it must not be is a guess.
 */
export const columnHeadSx: SxProps<Theme> = {
    flex: 1,
    minWidth: 0,
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[400],
};

/** The circular icon badge that heads a subject. Precedent: the configuration item badge. */
export const subjectBadgeSx: SxProps<Theme> = {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main,
    background: alpha(theme.palette.primary.main, 0.1),
};

/** A list row that behaves like a link, with its trailing controls revealed on hover or focus. */
export const listRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2.5,
    py: 1.5,
    borderBottom: `1px solid ${theme.palette.neutral[100]}`,
    transition: 'background-color 120ms ease',
    '&:last-of-type': {borderBottom: 'none'},
    '&:hover': {backgroundColor: theme.palette.primaryColor.A50},
    // `focus-within` is not decoration: without it the row action is unreachable from the keyboard.
    '& .rowAction': {opacity: 0, transition: 'opacity 0.3s ease'},
    '&:hover .rowAction, &:focus-within .rowAction': {opacity: 1},
    // A finger cannot hover, and 760px of fixed columns do not fit a phone. Below a desktop the row's
    // action is always visible and the columns wrap onto as many lines as they need.
    [theme.breakpoints.down('md')]: {
        '& .rowAction': {opacity: 1},
        flexWrap: 'wrap',
        rowGap: 1,
    },
};

/**
 * The columns these lists are built from.
 *
 * Fixed on a desktop, because the point of the tabs is that the same columns sit in the same places on
 * both. Below a desktop they give that up: a name column of 300, a scope of 200, two counts of 88 and a
 * badge and a chevron add up to some 760px of row, and the half that did not fit was cut off by the
 * card rather than scrolled to.
 *
 * No `SxProps` annotation on purpose - that type is a union including arrays, so the moment one of
 * these is spread to add a colour TypeScript has to consider the array case and refuses.
 */
export const nameColSx = {
    minWidth: 0,
    width: 300,
    flex: 'none',
    [theme.breakpoints.down('md')]: {width: '100%', flex: '1 1 100%'},
};

export const scopeColSx = {
    minWidth: 0,
    width: 200,
    flex: 'none',
    [theme.breakpoints.down('md')]: {width: 'auto'},
};

export const kindColSx = {
    width: 120,
    flex: 'none',
    [theme.breakpoints.down('md')]: {width: 'auto'},
};

export const countColSx = {
    width: 88,
    flex: 'none',
    // Held off the chevron: hard against it the number reads as part of the control rather than as a
    // value belonging to the row.
    mr: 3,
    fontWeight: 600,
    fontSize: '0.9375rem',
    color: theme.palette.neutral[500],
    [theme.breakpoints.down('md')]: {width: 'auto', mr: 0},
};

/**
 * The heading row over those columns, shared by the two tabs.
 *
 * It disappears once the rows wrap, because it names positions that no longer exist - a heading over a
 * three-line block points at nothing.
 */
export const listHeaderRowSx = {
    ...listRowSx,
    py: 1.25,
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: theme.palette.background.default,
    '&:hover': {backgroundColor: theme.palette.background.default},
    [theme.breakpoints.down('md')]: {display: 'none'},
};

/** The bar that appears once a role has unsaved edits. Precedent: the AI usage bar. */
export const stickyBarSx: SxProps<Theme> = {
    position: 'sticky',
    bottom: 0,
    zIndex: 2,
    mt: 3,
    px: 2.5,
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flexWrap: 'wrap',
    bgcolor: 'rgba(255, 255, 255, 0.92)',
    backdropFilter: 'blur(10px)',
    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
    borderRadius: '14px 14px 0 0',
    boxShadow: '0 -2px 12px rgba(0, 0, 0, 0.06)',
};

/** A subject band heading a list of permissions. */
export const subjectBandSx: SxProps<Theme> = {
    px: 2.5,
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    backgroundColor: theme.palette.neutral[50],
    borderBottom: `1px solid ${theme.palette.neutral[100]}`,
};
